import { useState } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

interface EducationProps {
  language: 'pt' | 'en';
}

export default function Education({ language }: EducationProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  const handleTabChange = (index: number) => {
    if (index === activeTab) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(index);
      setIsTransitioning(false);
    }, 200);
  };

  const content = {
    pt: {
      title: 'Método e Disciplina',
      subtitle: 'Consórcio como instrumento de planejamento patrimonial estruturado',
      tabs: [
        {
          label: 'Planejamento',
          title: 'Disciplina Financeira',
          description: 'Método de poupança programada que estrutura a aquisição de ativos sem incidência de juros. Exercício de organização financeira para objetivos de médio e longo prazo.'
        },
        {
          label: 'Execução',
          title: 'Estratégia: NÃO ao imediatismo',
          description: 'Adequado para quem planeja com antecedência. Exige disciplina e paciência para aguardar contemplação via sorteio ou lance estratégico.'
        },
        {
          label: 'Estrutura',
          title: 'Regulação e Transparência',
          description: 'Sistema regulamentado pelo Banco Central. Sem juros — apenas taxa de administração. Alternativa econômica para construção patrimonial.'
        },
        {
          label: 'Governança',
          title: 'Controle e Sobriedade',
          description: 'Poupança colaborativa para aquisição de bens. Promove controle orçamentário e evita endividamento por impulso.'
        }
      ]
    },
    en: {
      title: 'Method and Discipline',
      subtitle: 'Consortium as a structured wealth planning instrument',
      tabs: [
        {
          label: 'Planning',
          title: 'Financial Discipline',
          description: 'Programmed savings method that structures asset acquisition without interest charges. Financial organization exercise for medium and long-term objectives.'
        },
        {
          label: 'Execution',
          title: 'Strategy: NOT to Immediacy',
          description: 'Suitable for those who plan ahead. Requires discipline and patience to await award via draw or strategic bid.'
        },
        {
          label: 'Structure',
          title: 'Regulation and Transparency',
          description: 'System regulated by the Central Bank. No interest — only administration fee. Economic alternative for wealth building.'
        },
        {
          label: 'Governance',
          title: 'Control and Sobriety',
          description: 'Collaborative savings for asset acquisition. Promotes budget control and avoids impulsive debt.'
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section ref={sectionRef} className="relative bg-[#FBF8F3] border-b border-[#E7E1D9] overflow-hidden">
      {/* Decorative image — right half, grayscale, low opacity */}
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none select-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_bad23f58597a8bd5f46ee389360a2d19_19fd3205.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{
            filter: 'grayscale(100%)',
            opacity: isVisible ? 0.06 : 0,
            transform: isVisible ? 'scale(1.25)' : 'scale(1.32)',
            transition: 'opacity 1.2s ease-out, transform 1.4s ease-out',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-sm uppercase tracking-widest text-[#2E2E2E] mb-4 font-semibold">
            {t.title}
          </h2>
          <p className="text-2xl text-[#0B0B0B] mb-16 leading-relaxed">
            {t.subtitle}
          </p>

          {/* Tabs Navigation */}
          <div className="flex flex-wrap gap-3 mb-12 border-b border-[#E7E1D9] pb-6">
            {t.tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(index)}
                className={`px-6 py-3 text-sm font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === index
                    ? 'bg-[#F97700] text-white'
                    : 'bg-transparent text-[#2E2E2E] hover:text-[#F97700] border border-[#E7E1D9] hover:border-[#F97700]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[240px] relative">
            {t.tabs.map((tab, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                  activeTab === index && !isTransitioning
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
              >
                <h3 className="text-3xl font-semibold text-[#0B0B0B] mb-6">
                  {tab.title}
                </h3>
                <p className="text-lg text-[#2E2E2E] leading-relaxed">
                  {tab.description}
                </p>
              </div>
            ))}
          </div>

          {/* Key Points Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-20 pt-20 border-t border-[#E7E1D9]">
            <div className="space-y-3">
              <div className="w-8 h-0.5 bg-[#F97700]"></div>
              <h4 className="text-sm uppercase tracking-widest text-[#2E2E2E] font-semibold">
                {language === 'pt' ? 'Poupança Programada' : 'Programmed Savings'}
              </h4>
              <p className="text-base text-[#0B0B0B] leading-relaxed">
                {language === 'pt' 
                  ? 'Método estruturado para quem tem dificuldade em poupar. Compromisso mensal que constrói capacidade de aquisição.'
                  : 'Structured method for those who struggle to save. Monthly commitment that builds acquisition capacity.'}
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-8 h-0.5 bg-[#F97700]"></div>
              <h4 className="text-sm uppercase tracking-widest text-[#2E2E2E] font-semibold">
                {language === 'pt' ? 'Sem Juros' : 'No Interest'}
              </h4>
              <p className="text-base text-[#0B0B0B] leading-relaxed">
                {language === 'pt'
                  ? 'Apenas taxa de administração. Economia significativa comparada a financiamentos tradicionais com juros.'
                  : 'Only administration fee. Significant savings compared to traditional interest-bearing financing.'}
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-8 h-0.5 bg-[#F97700]"></div>
              <h4 className="text-sm uppercase tracking-widest text-[#2E2E2E] font-semibold">
                {language === 'pt' ? 'Regulamentação' : 'Regulation'}
              </h4>
              <p className="text-base text-[#0B0B0B] leading-relaxed">
                {language === 'pt'
                  ? 'Sistema supervisionado pelo Banco Central. Transparência nas regras, taxas e formas de contemplação.'
                  : 'System supervised by the Central Bank. Transparency in rules, fees and award methods.'}
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-8 h-0.5 bg-[#F97700]"></div>
              <h4 className="text-sm uppercase tracking-widest text-[#2E2E2E] font-semibold">
                {language === 'pt' ? 'Planejamento Futuro' : 'Future Planning'}
              </h4>
              <p className="text-base text-[#0B0B0B] leading-relaxed">
                {language === 'pt'
                  ? 'Ideal para objetivos de médio e longo prazo. Construção patrimonial estável e econômica.'
                  : 'Ideal for medium and long-term objectives. Stable and economical wealth building.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}